import { CalendarClock, Check, Copy, ListChecks, MousePointerClick, Users, Vote, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { api, type ResponseActivity, type ResponsePerson } from "../../api";
import { Avatar } from "../../components/primitives";
import { timezoneEventSummary } from "./ActionsEditor";
import { SlackFieldPreview } from "./MessagePreview";

type Results = Awaited<ReturnType<typeof api.results>>;
type ResultComponent = Results["components"][number];

function timeAnswerForViewer(value: string, timezone: string | undefined, respondedAt: string) {
  if (!timezone || !/^\d{2}:\d{2}$/.test(value)) return value;
  try {
    const anchor = new Date(respondedAt);
    const parts = Object.fromEntries(
      new Intl.DateTimeFormat("en-CA", {
        timeZone: timezone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hourCycle: "h23",
      })
        .formatToParts(anchor)
        .map((part) => [part.type, part.value]),
    );
    const [hour, minute] = value.split(":").map(Number);
    const intended = Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day), hour, minute);
    let instant = intended;
    for (let pass = 0; pass < 2; pass += 1) {
      const rendered = Object.fromEntries(
        new Intl.DateTimeFormat("en-CA", {
          timeZone: timezone,
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          hourCycle: "h23",
        })
          .formatToParts(new Date(instant))
          .map((part) => [part.type, part.value]),
      );
      instant +=
        intended -
        Date.UTC(
          Number(rendered.year),
          Number(rendered.month) - 1,
          Number(rendered.day),
          Number(rendered.hour),
          Number(rendered.minute),
        );
    }
    const local = new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" }).format(new Date(instant));
    return Intl.DateTimeFormat().resolvedOptions().timeZone === timezone
      ? `${local} · ${timezone}`
      : `${local} your time · ${value} ${timezone}`;
  } catch {
    return `${value}${timezone ? ` · ${timezone}` : ""}`;
  }
}

function answerValue(answer: { value: string; type?: string; timezone?: string }, respondedAt: string) {
  return answer.type === "time" ? timeAnswerForViewer(answer.value, answer.timezone, respondedAt) : answer.value;
}

function responseActors(component: { people: ResponsePerson[]; activity: ResponseActivity[] }) {
  const actors = new Map<
    string,
    { id: string; name: string; avatarUrl?: string; mention: string; count: number; lastAt?: string }
  >();
  for (const activity of component.activity)
    actors.set(activity.id, {
      id: activity.id,
      name: activity.name,
      avatarUrl: activity.avatarUrl,
      mention: activity.mention,
      count: activity.count,
      lastAt: activity.lastAt,
    });
  for (const person of component.people) {
    const existing = actors.get(person.id);
    if (existing) {
      if (!component.activity.some((activity) => activity.id === person.id)) existing.count += 1;
      continue;
    }
    actors.set(person.id, {
      id: person.id,
      name: person.name,
      avatarUrl: person.avatarUrl,
      mention: person.mention,
      count: 1,
      lastAt: person.respondedAt,
    });
  }
  return [...actors.values()].sort((left, right) => (right.lastAt ?? "").localeCompare(left.lastAt ?? ""));
}

function totalPresses(component: { people: ResponsePerson[]; activity: ResponseActivity[] }) {
  const activityTotal = component.activity.reduce((sum, activity) => sum + activity.count, 0);
  return activityTotal || component.people.length;
}

function interactionCount(activity: ResponseActivity[], userId: string) {
  return activity.find((entry) => entry.id === userId)?.count ?? 1;
}

function CopyMentions({
  copyKey,
  people,
  copied,
  onCopy,
}: {
  copyKey: string;
  people: Array<{ id: string; mention: string }>;
  copied: string | null;
  onCopy: (key: string, people: Array<{ id: string; mention: string }>) => void;
}) {
  return (
    <button type="button" className="copy-mentions" disabled={!people.length} onClick={() => onCopy(copyKey, people)}>
      {copied === copyKey ? <Check size={12} /> : <Copy size={12} />}
      {copied === copyKey ? "Copied" : "Copy mentions"}
    </button>
  );
}

function PersonLine({
  person,
  count = 1,
  showTimezone = false,
}: {
  person: { name: string; avatarUrl?: string; timezone?: string };
  count?: number;
  showTimezone?: boolean;
}) {
  return (
    <span className="result-person-line">
      <Avatar name={person.name} src={person.avatarUrl} size={22} />
      <span>{person.name}</span>
      {(showTimezone && person.timezone) || count > 1 ? (
        <small>
          {showTimezone && person.timezone}
          {showTimezone && person.timezone && count > 1 ? " · " : ""}
          {count > 1 ? `×${count}` : ""}
        </small>
      ) : null}
    </span>
  );
}

function FormResults({
  component,
  copied,
  onCopy,
}: {
  component: ResultComponent;
  copied: string | null;
  onCopy: (key: string, people: Array<{ id: string; mention: string }>) => void;
}) {
  const submissions = component.people.filter(
    (person) => person.submitted && (person.answers?.length || person.answer),
  );
  const actors = responseActors(component);
  return (
    <article className="result-section form-result-section">
      <header className="result-section-head">
        <ListChecks size={14} />
        <strong>
          <SlackFieldPreview text={component.label} mode="emoji" />
        </strong>
        <span>
          {submissions.length} {submissions.length === 1 ? "submission" : "submissions"} · {totalPresses(component)}{" "}
          {totalPresses(component) === 1 ? "open" : "opens"}
        </span>
      </header>
      <div className="form-submissions">
        {submissions.map((person, index) => (
          <div className="form-submission" key={person.responseId}>
            <div className="form-submitter">
              <Avatar name={person.name} src={person.avatarUrl} size={26} />
              <strong>{person.name}</strong>
              <time>
                {new Intl.DateTimeFormat(undefined, { dateStyle: "short", timeStyle: "short" }).format(
                  new Date(person.respondedAt),
                )}
              </time>
              {submissions.filter((entry) => entry.id === person.id).length > 1 && (
                <small>#{submissions.slice(0, index + 1).filter((entry) => entry.id === person.id).length}</small>
              )}
            </div>
            {person.answer && (
              <div className="form-answer">
                <SlackFieldPreview text={person.answer} mode="mrkdwn" />
              </div>
            )}
            {person.answers?.map((answer) => (
              <div className="form-answer" key={`${person.responseId}_${answer.label}`}>
                <b>
                  <SlackFieldPreview text={answer.label} mode="emoji" />
                </b>
                <span>
                  <SlackFieldPreview text={answerValue(answer, person.respondedAt) || "—"} mode="mrkdwn" />
                </span>
              </div>
            ))}
          </div>
        ))}
        {!submissions.length && <small className="result-empty">No submissions yet</small>}
      </div>
      <footer>
        {actors.map((person) => (
          <PersonLine key={person.id} person={person} count={person.count} />
        ))}
        <CopyMentions copyKey={component.id} people={actors} copied={copied} onCopy={onCopy} />
      </footer>
    </article>
  );
}

function componentGroupName(component: ResultComponent) {
  const raw = component.config.userGroup;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const name = (raw as Record<string, unknown>).name;
  return typeof name === "string" && name.trim() ? name : undefined;
}

function UserGroupResults({
  component,
  copied,
  onCopy,
}: {
  component: ResultComponent;
  copied: string | null;
  onCopy: (key: string, people: Array<{ id: string; mention: string }>) => void;
}) {
  const actors = responseActors(component);
  const groupName = componentGroupName(component);
  return (
    <article className="result-section ping-group-result-section">
      <header className="result-section-head">
        <Users size={14} />
        <strong>
          <SlackFieldPreview text={component.label} mode="emoji" />
        </strong>
        <span>
          {groupName ? `@${groupName} · ` : ""}
          {actors.length} {actors.length === 1 ? "member" : "members"}
        </span>
      </header>
      <div className="result-person-list">
        {actors.map((person) => (
          <PersonLine key={person.id} person={person} count={person.count} />
        ))}
        {!actors.length && <small className="result-empty">No one has joined yet</small>}
      </div>
      <CopyMentions copyKey={component.id} people={actors} copied={copied} onCopy={onCopy} />
    </article>
  );
}

const behaviorNames: Record<string, string> = {
  open_url: "Link",
  show_ephemeral: "Private reply",
  record: "Response",
  add_to_list: "Response",
  notify_sender: "Message to you",
  dm_clicker: "DM",
  join_user_group: "User group",
};

export function ResultsPanel({ messageId }: { messageId: string }) {
  const [results, setResults] = useState<Results | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [voteChange, setVoteChange] = useState<{ id: string; closed: boolean } | null>(null);
  const [voteChangeError, setVoteChangeError] = useState("");
  const [voteChanging, setVoteChanging] = useState(false);
  useEffect(() => {
    api
      .results(messageId)
      .then(setResults)
      .catch(() => undefined);
    const timer = window.setInterval(
      () =>
        api
          .results(messageId)
          .then(setResults)
          .catch(() => undefined),
      15_000,
    );
    return () => window.clearInterval(timer);
  }, [messageId]);
  const copyPeople = async (key: string, people: Array<{ id: string; mention: string }>) => {
    await navigator.clipboard.writeText(
      [...new Map(people.map((person) => [person.id, person.mention])).values()].join(" "),
    );
    setCopied(key);
    window.setTimeout(() => setCopied(null), 1600);
  };
  const applyVoteChange = async () => {
    if (!voteChange) return;
    setVoteChanging(true);
    setVoteChangeError("");
    try {
      const updated = await api.setVoteClosed(messageId, voteChange.id, voteChange.closed);
      setResults((current) =>
        current
          ? {
              ...current,
              votes: current.votes.map((vote) =>
                vote.id === voteChange.id ? { ...vote, closed: updated.closed } : vote,
              ),
            }
          : current,
      );
      setVoteChange(null);
    } catch (error) {
      setVoteChangeError(error instanceof Error ? error.message : "Could not update this vote.");
    } finally {
      setVoteChanging(false);
    }
  };
  const responderCount = useMemo(() => {
    if (!results) return 0;
    return new Set([
      ...results.components.flatMap((component) => component.activity.map((person) => person.id)),
      ...results.components.flatMap((component) => component.people.map((person) => person.id)),
    ]).size;
  }, [results]);
  if (!results) return null;
  const genericComponents = results.components.filter(
    (component) => component.behavior !== "vote" && component.behavior !== "rsvp",
  );
  if (!genericComponents.length && !results.votes.length && !results.rsvps.length) return null;
  return (
    <section className="results-panel" aria-label="Message responses">
      <header className="results-heading">
        <strong>Responses</strong>
        <span>{responderCount ? `${responderCount} ${responderCount === 1 ? "person" : "people"}` : "None yet"}</span>
      </header>

      {results.votes.map((vote) => {
        const voters = [
          ...new Map(vote.options.flatMap((option) => option.people).map((person) => [person.id, person])).values(),
        ];
        return (
          <article className="result-section vote-result-section" key={vote.id}>
            <header className="result-section-head">
              <Vote size={14} />
              <strong>
                <SlackFieldPreview text={vote.question} mode="mrkdwn" />
              </strong>
              <div className="sent-actions result-vote-state">
                <span>{vote.closed ? "Closed" : `${voters.length} ${voters.length === 1 ? "voter" : "voters"}`}</span>
                <button
                  type="button"
                  onClick={() => {
                    setVoteChangeError("");
                    setVoteChange({ id: vote.id, closed: !vote.closed });
                  }}
                >
                  {vote.closed ? "Reopen" : "Close vote"}
                </button>
              </div>
            </header>
            <div className="result-vote-options">
              {vote.options.map((option) => {
                const percentage = voters.length ? Math.round((option.people.length / voters.length) * 100) : 0;
                return (
                  <div className="result-vote-option" key={option.id}>
                    <div className="result-vote-label">
                      <strong>
                        <SlackFieldPreview text={option.label} mode="emoji" />
                      </strong>
                      <span>
                        {percentage}% · {option.people.length}
                      </span>
                    </div>
                    {option.description && <SlackFieldPreview text={option.description} mode="mrkdwn" />}
                    <div
                      className="result-vote-bar"
                      role="progressbar"
                      aria-label={`${option.label} vote share`}
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-valuenow={percentage}
                    >
                      <span style={{ width: `${percentage}%` }} />
                    </div>
                    <div className="result-person-list">
                      {option.people.map((person) => (
                        <PersonLine
                          key={person.responseId}
                          person={person}
                          count={interactionCount(option.activity, person.id)}
                        />
                      ))}
                      {!option.people.length && <small className="result-empty">No votes</small>}
                    </div>
                    <CopyMentions
                      copyKey={`vote_${option.id}`}
                      people={option.people}
                      copied={copied}
                      onCopy={copyPeople}
                    />
                  </div>
                );
              })}
            </div>
          </article>
        );
      })}

      {results.rsvps.map((rsvp) => (
        <article className="result-section rsvp-result-section" key={rsvp.id}>
          <header className="result-section-head">
            <CalendarClock size={14} />
            <strong>
              <SlackFieldPreview text={rsvp.eventTitle} mode="emoji" />
            </strong>
            <span>{rsvp.people.length} going</span>
          </header>
          {rsvp.eventStartsAt && rsvp.eventTimezone && (
            <p className="result-event-time">{timezoneEventSummary(rsvp.eventStartsAt, rsvp.eventTimezone)}</p>
          )}
          <div className="result-person-list">
            {rsvp.people.map((person) => (
              <PersonLine
                key={person.responseId}
                person={person}
                count={interactionCount(rsvp.activity, person.id)}
                showTimezone
              />
            ))}
            {!rsvp.people.length && <small className="result-empty">No RSVPs</small>}
          </div>
          <CopyMentions copyKey={`rsvp_${rsvp.id}`} people={rsvp.people} copied={copied} onCopy={copyPeople} />
        </article>
      ))}

      {genericComponents.map((component) => {
        if (["open_form", "collect_input"].includes(component.behavior))
          return <FormResults key={component.id} component={component} copied={copied} onCopy={copyPeople} />;
        if (component.behavior === "join_user_group")
          return <UserGroupResults key={component.id} component={component} copied={copied} onCopy={copyPeople} />;
        const actors = responseActors(component);
        return (
          <article className="result-section button-result-section" key={component.id}>
            <header className="result-section-head">
              <MousePointerClick size={14} />
              <strong>
                <SlackFieldPreview text={component.label} mode="emoji" />
              </strong>
              <span>
                {behaviorNames[component.behavior] ?? "Button"} · {totalPresses(component)}{" "}
                {totalPresses(component) === 1 ? "click" : "clicks"}
              </span>
            </header>
            <div className="result-person-list">
              {actors.map((person) => (
                <PersonLine key={person.id} person={person} count={person.count} />
              ))}
              {!actors.length && <small className="result-empty">No clicks</small>}
            </div>
            <CopyMentions copyKey={component.id} people={actors} copied={copied} onCopy={copyPeople} />
          </article>
        );
      })}

      {voteChange && (
        <div className="new-draft-layer vote-state-dialog-layer">
          <button
            type="button"
            className="new-draft-backdrop"
            aria-label="Cancel vote change"
            disabled={voteChanging}
            onClick={() => setVoteChange(null)}
          />
          <section
            className="new-draft vote-state-dialog"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="vote-confirm-title"
          >
            <header>
              <div>
                <span>Vote</span>
                <strong id="vote-confirm-title">{voteChange.closed ? "Close this vote?" : "Reopen this vote?"}</strong>
              </div>
              <button
                type="button"
                className="vote-state-dialog-close"
                aria-label="Cancel"
                disabled={voteChanging}
                onClick={() => setVoteChange(null)}
              >
                <X size={17} />
              </button>
            </header>
            <div className="vote-state-dialog-body">
              <p>
                {voteChange.closed
                  ? "The buttons will disappear. Results stay visible."
                  : "The voting buttons will return."}
              </p>
              {voteChangeError && <p className="new-draft-error">{voteChangeError}</p>}
            </div>
            <footer>
              <button type="button" disabled={voteChanging} onClick={() => setVoteChange(null)}>
                Cancel
              </button>
              <button type="button" className="new-draft-create" disabled={voteChanging} onClick={applyVoteChange}>
                {voteChanging ? "Updating…" : voteChange.closed ? "Close vote" : "Reopen vote"}
              </button>
            </footer>
          </section>
        </div>
      )}
    </section>
  );
}
